import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem('admin-panel-theme') as ThemeMode;
    if (savedTheme) {
      return savedTheme;
    }
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  const [isSwitching, setIsSwitching] = useState(false);

  const setTheme = (newTheme: ThemeMode) => {
    if (isSwitching) return; // Prevent rapid switching
    
    setIsSwitching(true);
    setThemeState(newTheme);
    localStorage.setItem('admin-panel-theme', newTheme);
    
    // Reset switching state after animation
    setTimeout(() => setIsSwitching(false), 200);
  };

  const toggleTheme = () => {
    if (isSwitching) return; // Prevent rapid switching
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('admin-panel-theme')) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document with optimized switching
  useEffect(() => {
    // Add theme-switching class to disable transitions
    document.body.classList.add('theme-switching');
    
    // Apply theme changes
    document.documentElement.setAttribute('data-theme', theme);
    document.body.className = `theme-${theme}`;
    
    // Remove theme-switching class after a short delay
    const timer = setTimeout(() => {
      document.body.classList.remove('theme-switching');
    }, 50);
    
    return () => clearTimeout(timer);
  }, [theme]);

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
